/**
 * Development pre-fill for the internship application form.
 *
 * Set REACT_APP_PREFILL_INTERNSHIP=true in .env.development.local and the
 * application form on /training-internship opens with plausible test data, so
 * you can submit in one click instead of retyping twenty fields. It is the
 * longest form on the site, which is why it is the only one that gets this.
 *
 * No other form is affected — the enquiry forms, the newsletter and the admin
 * sign-in all behave normally.
 *
 * Two locks, deliberately. The flag alone is not enough: this also requires
 * NODE_ENV === 'development', which Create React App sets automatically and
 * which is always 'production' for `npm run build`. So even if the flag is left
 * switched on in a production environment file, a production build ignores it
 * and ships empty forms. Test-only conveniences that can reach real users are
 * exactly the kind of thing that quietly does damage.
 */

const ENABLED =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_PREFILL_INTERNSHIP === 'true';

export const isPrefillEnabled = () => ENABLED;

// Announce it once, so a confusing pre-filled form is never a mystery.
if (ENABLED && typeof window !== 'undefined' && !window.__spPrefillLogged) {
  window.__spPrefillLogged = true;
  console.warn(
    '[dev] Internship form pre-fill is ON (REACT_APP_PREFILL_INTERNSHIP=true). ' +
    'This never applies to a production build.'
  );
}

/** Unique each call, so the duplicate-application guard does not reject you. */
const stamp = () => `${Date.now().toString(36)}${Math.floor(Math.random() * 100)}`;

const pick = (list) => list[Math.floor(Math.random() * list.length)];

const FIRST = ['Aarav', 'Diya', 'Vihaan', 'Ananya', 'Kabir', 'Ishita', 'Arjun', 'Meera'];
const LAST = ['Sharma', 'Verma', 'Gupta', 'Singh', 'Jain', 'Agarwal', 'Rathore'];
const COLLEGES = ['MNIT Jaipur', 'JECRC University', 'Poornima College of Engineering',
  'Manipal University Jaipur', 'SKIT Jaipur'];

/**
 * Merges sample values over the real defaults when pre-fill is on, and returns
 * the defaults untouched when it is off.
 *
 *   const initialValues = withPrefill(EMPTY, contactSample);
 */
export function withPrefill(defaults, sample) {
  if (!ENABLED) return defaults;
  return { ...defaults, ...(typeof sample === 'function' ? sample() : sample) };
}

/* ---------------------------------------------------------------- samples -- */

export const internshipSample = () => {
  const first = pick(FIRST);
  const last = pick(LAST);
  const start = new Date();
  start.setDate(start.getDate() + 21);

  return {
    first_name: first,
    last_name: last,
    // Unique every time: the API blocks a second application for the same
    // track from the same address within a month.
    email: `applicant.${stamp()}@example.com`,
    phone: '9876543210',
    city: 'Jaipur',
    state: 'Rajasthan',
    college: pick(COLLEGES),
    degree: 'B.Tech',
    branch: 'Computer Science',
    graduation_year: String(new Date().getFullYear() + 1),
    cgpa: '8.4',
    programme_type: 'training_internship',
    track_slug: 'software-development',
    preferred_start: start.toISOString().slice(0, 10),
    work_mode: 'hybrid',
    heard_from: 'College / TPO',
    linkedin_url: 'https://linkedin.com/in/test-candidate',
    github_url: 'https://github.com/test-candidate',
    portfolio_url: '',
    cover_note:
      'Pre-filled test application. I want to work on backend systems and have ' +
      'built a small inventory API using Node and Postgres.',
    resume: sampleResume(),
  };
};

/**
 * A real File object holding a minimal valid PDF, so the upload path is
 * genuinely exercised rather than skipped.
 */
export function sampleResume() {
  if (!ENABLED || typeof File === 'undefined') return null;
  const pdf =
    '%PDF-1.4\n1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj\n' +
    '2 0 obj<</Type/Pages/Kids[]/Count 0>>endobj\n' +
    'trailer<</Root 1 0 R>>\n%%EOF';
  return new File([pdf], 'sample-resume.pdf', { type: 'application/pdf' });
}


export default { isPrefillEnabled, withPrefill, internshipSample, sampleResume };
